<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useNativeFieldOptions } from 'dashboard/composables/useNativeFieldOptions';
import Button from 'dashboard/components-next/button/Button.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import draggable from 'vuedraggable';
import OptionListEditor from './OptionListEditor.vue';

const props = defineProps({
  folder: { type: Object, required: true },
  activeCategory: { type: String, default: 'sales' },
});

const emit = defineEmits(['add-field', 'edit-field', 'delete-field']);

// Campos nativos fixos da pasta Principal
const NATIVE_FIELDS = [
  {
    key: 'loss_reason',
    label: 'Motivo de Perda',
    typeGroup: 'list',
    typeVariant: 'single',
    category: 'sales',
  },
  {
    key: 'cancel_reason',
    label: 'Motivo de Cancelamento',
    typeGroup: 'list',
    typeVariant: 'single',
    category: 'post_sales',
  },
  {
    key: 'waiting_reason',
    label: 'Motivo de Aguardando',
    typeGroup: 'list',
    typeVariant: 'single',
    category: 'post_sales',
  },
  { key: 'source', label: 'Fonte', typeGroup: 'list', typeVariant: 'single' },
  {
    key: 'product',
    label: 'Produto',
    typeGroup: 'list',
    typeVariant: 'multiple',
  },
  { key: 'city', label: 'Cidade', typeGroup: 'list', typeVariant: 'single' },
  {
    key: 'contact',
    label: 'Contato',
    typeGroup: 'list',
    typeVariant: 'single',
  },
  { key: 'company', label: 'Empresa', typeGroup: 'text', typeLabel: 'texto' },
  {
    key: 'phone',
    label: 'Telefone',
    typeGroup: 'number',
    typeLabel: 'telefone',
  },
  { key: 'email', label: 'E-mail', typeGroup: 'text', typeLabel: 'e-mail' },
  {
    key: 'pipeline',
    label: 'Pipeline',
    typeGroup: 'list',
    typeVariant: 'single',
  },
  { key: 'stage', label: 'Etapa', typeGroup: 'list', typeVariant: 'single' },
  { key: 'status', label: 'Status', typeGroup: 'list', typeVariant: 'single' },
  { key: 'forecast', label: 'Previsão', typeGroup: 'date', typeLabel: 'data' },
  { key: 'value', label: 'Valor', typeGroup: 'number', typeLabel: 'monetário' },
  {
    key: 'assignee',
    label: 'Responsável',
    typeGroup: 'list',
    typeVariant: 'single',
  },
  {
    key: 'watchers',
    label: 'Seguidores',
    typeGroup: 'list',
    typeVariant: 'multiple',
  },
  { key: 'tags', label: 'Tags', typeGroup: 'list', typeVariant: 'multiple' },
];

const isPrincipal = computed(() => props.folder.slug === 'principal');

const visibleNativeFields = computed(() =>
  NATIVE_FIELDS.filter(
    field => !field.category || field.category === props.activeCategory
  )
);

const { t } = useI18n();
const store = useStore();
const { currentAccount } = useAccount();

const folders = computed(() => store.getters['kanbanFolders/folders']);
const allAttributes = computed(() =>
  store.getters['attributes/getAttributesByModel']('opportunity_attribute')
);
const folderAttributes = computed(() =>
  allAttributes.value
    .filter(a => a.folder_id === props.folder.id)
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
);

const dragging = ref(false);
const movingId = ref(null);
const openMoveId = ref(null);

const localAttributes = ref([]);

// Sync local list when not dragging
const syncLocal = () => {
  localAttributes.value = [...folderAttributes.value];
};

// Watch folderAttributes but skip while dragging
import { watch } from 'vue';
watch(
  folderAttributes,
  val => {
    if (!dragging.value) localAttributes.value = [...val];
  },
  { immediate: true }
);

async function onDragEnd() {
  dragging.value = false;
  const updates = localAttributes.value.map((attr, index) =>
    store.dispatch('attributes/update', { id: attr.id, position: index })
  );
  try {
    await Promise.all(updates);
  } catch {
    useAlert(t('KANBAN.FOLDERS.ERROR'));
    syncLocal();
  }
}

function toggleMoveDropdown(id) {
  openMoveId.value = openMoveId.value === id ? null : id;
}

async function moveToFolder(attribute, targetFolderId) {
  movingId.value = attribute.id;
  openMoveId.value = null;
  try {
    await store.dispatch('attributes/update', {
      id: attribute.id,
      kanban_folder_id: targetFolderId,
    });
    useAlert(t('KANBAN.FOLDERS.MOVE_SUCCESS'));
  } catch {
    useAlert(t('KANBAN.FOLDERS.ERROR'));
  } finally {
    movingId.value = null;
  }
}

const otherFolders = computed(() =>
  folders.value.filter(
    f =>
      f.id !== props.folder.id &&
      (!f.category || f.category === props.activeCategory)
  )
);

const TYPE_LABELS = {
  text: 'texto',
  number: 'número',
  date: 'data',
  file: 'arquivo',
  text_area: 'texto longo',
  email: 'e-mail',
  link: 'link',
  currency: 'monetário',
  percent: 'percentual',
  phone: 'telefone',
  cpf: 'cpf',
  cnpj: 'cnpj',
  list: 'lista',
  multi_list: 'lista múltipla',
  text_list: 'campos',
  text_list_area: 'campos (texto longo)',
  checkbox: 'caixa',
  multi_checkbox: 'caixa',
  radio: 'seleção única',
};

const BASE_TYPES = new Set([
  'text',
  'number',
  'list',
  'checkbox',
  'text_list',
  'date',
  'file',
]);

const SUB_TO_BASE = {
  text_area: 'text',
  email: 'text',
  link: 'text',
  currency: 'number',
  percent: 'number',
  phone: 'number',
  cpf: 'number',
  cnpj: 'number',
  multi_list: 'list',
  radio: 'checkbox',
  multi_checkbox: 'checkbox',
  text_list_area: 'text_list',
};

const TYPE_TAG_CLASSES = {
  text: 'text-n-blue-11 bg-n-blue-2',
  number: 'text-n-teal-11 bg-n-teal-2',
  list: 'text-n-amber-11 bg-n-amber-2',
  checkbox: 'text-n-orange-11 bg-n-orange-2',
  text_list: 'text-n-iris-11 bg-n-iris-2',
  date: 'text-n-violet-11 bg-n-violet-2',
  file: 'text-n-ruby-11 bg-n-ruby-2',
};

function baseType(type) {
  return SUB_TO_BASE[type] ?? (BASE_TYPES.has(type) ? type : 'text');
}

function typeLabel(type) {
  return TYPE_LABELS[baseType(type)] ?? type;
}

function typeTagClass(type) {
  return TYPE_TAG_CLASSES[baseType(type)] ?? TYPE_TAG_CLASSES.text;
}

function subLabel(type) {
  if (BASE_TYPES.has(type)) return null;
  const sub = TYPE_LABELS[type] ?? null;
  if (sub === typeLabel(type)) return null;
  return sub;
}

function nativeFieldTagClass(typeGroup) {
  if (!typeGroup) return 'text-n-slate-10 bg-n-surface-2';
  return TYPE_TAG_CLASSES[typeGroup] ?? TYPE_TAG_CLASSES.text;
}

function nativeSubLabel(field) {
  if (!field.typeGroup) return null;
  if (field.typeLabel === TYPE_LABELS[field.typeGroup]) return null;
  return field.typeLabel;
}

function nativeTypeActiveClass(typeGroup) {
  const map = {
    text: 'border-n-blue-6 text-n-blue-11 bg-n-blue-2',
    number: 'border-n-teal-6 text-n-teal-11 bg-n-teal-2',
    list: 'border-n-amber-6 text-n-amber-11 bg-n-amber-2',
    date: 'border-n-violet-6 text-n-violet-11 bg-n-violet-2',
    file: 'border-n-ruby-6 text-n-ruby-11 bg-n-ruby-2',
  };
  return map[typeGroup] ?? 'border-n-blue-6 text-n-blue-11 bg-n-blue-2';
}

// Modal de edição de descrição de campos nativos
const editingNativeField = ref(null);
const nativeFieldDescription = ref('');
const isSavingNative = ref(false);

const nativeFieldDescriptions = computed(
  () => currentAccount.value?.settings?.kanban_native_field_descriptions ?? {}
);

const { getOptions, saveOptions, renameOptionValue } = useNativeFieldOptions();

const EDITABLE_OPTION_FIELDS = [
  'source',
  'product',
  'loss_reason',
  'cancel_reason',
  'waiting_reason',
  'city',
];

const editingOptions = ref([]);

const isEditableOptionField = computed(() =>
  editingNativeField.value
    ? EDITABLE_OPTION_FIELDS.includes(editingNativeField.value.key)
    : false
);

function openNativeFieldEdit(field) {
  editingNativeField.value = field;
  nativeFieldDescription.value = nativeFieldDescriptions.value[field.key] ?? '';
  editingOptions.value = EDITABLE_OPTION_FIELDS.includes(field.key)
    ? getOptions(field.key).map(o => ({ ...o }))
    : [];
}

function closeNativeFieldEdit() {
  editingNativeField.value = null;
  nativeFieldDescription.value = '';
  editingOptions.value = [];
}

async function saveNativeFieldDescription() {
  isSavingNative.value = true;
  try {
    const updated = {
      ...nativeFieldDescriptions.value,
      [editingNativeField.value.key]: nativeFieldDescription.value,
    };
    await store.dispatch('accounts/update', {
      kanban_native_field_descriptions: updated,
      options: { silent: true },
    });

    if (isEditableOptionField.value) {
      const fieldKey = editingNativeField.value.key;
      const original = getOptions(fieldKey);
      const cleaned = editingOptions.value.filter(o => o.label?.trim());

      const renamePromises = cleaned
        .map(opt => {
          const originalMatch = original.find(o => o.key === opt.key);
          if (originalMatch && originalMatch.label !== opt.label) {
            return renameOptionValue(fieldKey, originalMatch.label, opt.label);
          }
          return null;
        })
        .filter(Boolean);

      await Promise.all(renamePromises);
      await saveOptions(fieldKey, cleaned);
    }

    closeNativeFieldEdit();
  } catch {
    useAlert(t('KANBAN.FOLDERS.ERROR'));
  } finally {
    isSavingNative.value = false;
  }
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Estado vazio só para pastas sem campos nativos -->
    <div
      v-if="!isPrincipal && !localAttributes.length"
      class="flex flex-col items-center justify-center py-20 gap-3 text-n-slate-9"
    >
      <span class="i-lucide-folder-open size-10" />
      <p class="text-sm">{{ t('KANBAN.FOLDERS.NO_FIELDS') }}</p>
      <Button
        sm
        :label="t('KANBAN.FOLDERS.ADD_FIELD')"
        @click="emit('add-field', folder)"
      />
    </div>

    <div
      v-else
      class="flex flex-col divide-y divide-n-weak border-t border-n-weak"
    >
      <!-- Campos nativos (somente pasta Principal) -->
      <template v-if="isPrincipal">
        <div
          v-for="field in visibleNativeFields"
          :key="field.key"
          class="flex items-center group"
        >
          <div class="flex-1 py-2">
            <div class="flex items-center gap-2 px-1">
              <span class="w-5 h-9 flex items-center justify-center shrink-0" />
              <div class="flex flex-1 items-center gap-2">
                <span class="text-sm font-medium text-n-slate-12">{{
                  field.label
                }}</span>
                <span
                  v-if="field.typeGroup"
                  class="text-[11px] leading-none font-medium px-1.5 py-1 rounded shrink-0 lowercase"
                  :class="nativeFieldTagClass(field.typeGroup)"
                >
                  {{ TYPE_LABELS[field.typeGroup] ?? field.typeGroup }}
                </span>
                <span
                  v-if="field.typeVariant"
                  class="text-[11px] leading-none font-medium text-n-gray-11 bg-n-gray-2 px-1.5 py-1 rounded shrink-0 lowercase"
                >
                  {{
                    field.typeVariant === 'multiple'
                      ? t('KANBAN.FOLDERS.SELECTION_MULTIPLE')
                      : t('KANBAN.FOLDERS.SELECTION_SINGLE')
                  }}
                </span>
                <span
                  v-else-if="nativeSubLabel(field)"
                  class="text-[11px] leading-none font-medium text-n-gray-11 bg-n-gray-2 px-1.5 py-1 rounded shrink-0 lowercase"
                >
                  {{ nativeSubLabel(field) }}
                </span>
                <span
                  v-if="nativeFieldDescriptions[field.key]"
                  class="text-[11px] text-n-slate-10 truncate"
                >
                  {{ nativeFieldDescriptions[field.key] }}
                </span>
              </div>
              <span
                v-if="EDITABLE_OPTION_FIELDS.includes(field.key)"
                class="text-[11px] leading-none font-medium px-1.5 py-1 rounded shrink-0 lowercase text-n-teal-11/70 bg-n-teal-2/50"
              >
                {{ t('KANBAN.FOLDERS.EDITABLE_BADGE') }}
              </span>
              <span
                class="text-[11px] leading-none font-medium px-1.5 py-1 rounded shrink-0 lowercase text-n-slate-10 bg-n-surface-2"
              >
                {{ t('KANBAN.FOLDERS.NATIVE_BADGE') }}
              </span>
              <div
                class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <button
                  class="w-9 h-9 flex items-center justify-center rounded text-n-slate-9 hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors"
                  @click="openNativeFieldEdit(field)"
                >
                  <span class="i-lucide-pencil size-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Modal de edição de campo nativo -->
      <woot-modal
        v-if="editingNativeField"
        show
        :on-close="closeNativeFieldEdit"
      >
        <div class="flex flex-col h-auto overflow-auto">
          <form
            class="flex w-full"
            @submit.prevent="saveNativeFieldDescription"
          >
            <div class="w-full">
              <!-- Aplicação (readonly) + Proporção (readonly) -->
              <div class="flex items-start justify-between gap-4 pt-4">
                <label class="w-2/5 block mb-0">
                  {{ $t('ATTRIBUTES_MGMT.ADD.FORM.MODEL.LABEL') }}
                  <select disabled>
                    <option>
                      {{ $t('KANBAN.FOLDERS.OPPORTUNITY_MODEL') }}
                    </option>
                  </select>
                </label>
              </div>

              <!-- Nome (readonly) -->
              <woot-input
                :model-value="editingNativeField.label"
                :label="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.LABEL')"
                type="text"
                readonly
              />

              <!-- Chave (readonly) -->
              <woot-input
                :model-value="editingNativeField.key"
                :label="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.LABEL')"
                type="text"
                readonly
              />

              <!-- Descrição (editável) -->
              <label>
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.LABEL') }}
                <textarea
                  v-model="nativeFieldDescription"
                  rows="3"
                  :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.DESC.PLACEHOLDER')"
                />
              </label>

              <!-- Opções (editável, key travada) -->
              <div v-if="isEditableOptionField" class="mb-4">
                <label class="mb-2 block text-sm font-medium text-n-slate-11">
                  {{ $t('KANBAN.FOLDERS.OPTIONS_LABEL') }}
                </label>
                <OptionListEditor v-model="editingOptions" lock-keys />
              </div>

              <!-- Tipo (readonly) -->
              <div class="mb-4">
                <label class="mb-2 block text-sm font-medium text-n-slate-11">
                  {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LABEL') }}
                </label>
                <div class="flex flex-wrap gap-2">
                  <span
                    class="px-3 py-1.5 text-sm rounded-lg border"
                    :class="nativeTypeActiveClass(editingNativeField.typeGroup)"
                  >
                    {{
                      TYPE_LABELS[editingNativeField.typeGroup] ??
                      editingNativeField.typeGroup
                    }}
                  </span>
                </div>
              </div>

              <!-- Botões -->
              <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
                <NextButton
                  faded
                  slate
                  type="reset"
                  :label="$t('ATTRIBUTES_MGMT.ADD.CANCEL_BUTTON_TEXT')"
                  @click.prevent="closeNativeFieldEdit"
                />
                <NextButton
                  type="submit"
                  :label="$t('ATTRIBUTES_MGMT.EDIT.UPDATE_BUTTON_TEXT')"
                  :is-loading="isSavingNative"
                />
              </div>
            </div>
          </form>
        </div>
      </woot-modal>

      <draggable
        v-model="localAttributes"
        item-key="id"
        handle=".field-drag-handle"
        ghost-class="opacity-40"
        drag-class="shadow-xl"
        class="flex flex-col divide-y divide-n-weak"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element: attribute }">
          <div
            class="flex items-center group cursor-pointer"
            @click="emit('edit-field', attribute)"
          >
            <div class="flex-1 py-2">
              <div class="flex items-center gap-2 px-1">
                <span
                  class="w-5 h-9 flex items-center justify-center shrink-0"
                  :class="
                    !attribute.attribute_key.startsWith('utm_')
                      ? 'field-drag-handle text-n-slate-7 hover:text-n-slate-10 cursor-grab active:cursor-grabbing transition-colors'
                      : ''
                  "
                >
                  <span
                    v-if="!attribute.attribute_key.startsWith('utm_')"
                    class="i-lucide-grip-vertical size-4"
                  />
                </span>
                <div class="flex flex-1 items-center gap-2">
                  <span class="text-sm font-medium text-n-slate-12">{{
                    attribute.attribute_display_name
                  }}</span>
                  <span
                    class="text-[11px] leading-none font-medium px-1.5 py-1 rounded shrink-0 lowercase"
                    :class="typeTagClass(attribute.attribute_display_type)"
                  >
                    {{ typeLabel(attribute.attribute_display_type) }}
                  </span>
                  <span
                    v-if="subLabel(attribute.attribute_display_type)"
                    class="text-[11px] leading-none font-medium text-n-gray-11 bg-n-gray-2 px-1.5 py-1 rounded shrink-0 lowercase"
                  >
                    {{ subLabel(attribute.attribute_display_type) }}
                  </span>
                </div>
                <span
                  v-if="attribute.attribute_key.startsWith('utm_')"
                  class="text-[11px] leading-none font-medium px-1.5 py-1 rounded shrink-0 lowercase text-n-slate-10 bg-n-surface-2"
                >
                  {{ t('KANBAN.FOLDERS.NATIVE_BADGE') }}
                </span>
                <span
                  v-else
                  class="text-[11px] leading-none font-medium px-1.5 py-1 rounded shrink-0 lowercase text-n-teal-11/70 bg-n-teal-2/50"
                >
                  {{ t('KANBAN.FOLDERS.EDITABLE_BADGE') }}
                </span>
                <div
                  class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  @click.stop
                  @mousedown.stop
                >
                  <button
                    class="w-9 h-9 flex items-center justify-center rounded text-n-slate-9 hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors"
                    @click="emit('edit-field', attribute)"
                  >
                    <span class="i-lucide-pencil size-4" />
                  </button>
                  <button
                    v-if="!attribute.attribute_key.startsWith('utm_')"
                    class="w-9 h-9 flex items-center justify-center rounded text-n-ruby-9 hover:bg-n-ruby-2 transition-colors"
                    @click="emit('delete-field', attribute)"
                  >
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                  <div
                    v-if="!attribute.attribute_key.startsWith('utm_')"
                    class="relative"
                  >
                    <button
                      class="w-9 h-9 flex items-center justify-center rounded text-n-slate-9 hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors"
                      :class="{
                        'opacity-50 pointer-events-none':
                          movingId === attribute.id,
                      }"
                      @click.stop="toggleMoveDropdown(attribute.id)"
                    >
                      <span class="i-lucide-move size-4" />
                    </button>
                    <div
                      v-if="openMoveId === attribute.id && otherFolders.length"
                      class="absolute right-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 min-w-[160px]"
                      @click.stop
                    >
                      <button
                        v-for="f in otherFolders"
                        :key="f.id"
                        type="button"
                        class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
                        @click="moveToFolder(attribute, f.id)"
                      >
                        {{ f.name }}
                      </button>
                    </div>
                    <div
                      v-if="openMoveId === attribute.id && !otherFolders.length"
                      class="absolute right-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 min-w-[160px]"
                    >
                      <p class="px-3 py-2 text-sm text-n-slate-10 italic">
                        {{ t('KANBAN.FOLDERS.NO_OTHER_FOLDERS') }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </draggable>
    </div>
  </div>
</template>
